import React from 'react';
import Decimal from 'decimal.js';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_NIGHT,
  propTypes,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const lineItemQuantity = lineItem =>
  lineItem?.units ? Number(lineItem.units) : lineItem?.quantity ? Number(lineItem.quantity) : 0;

/**
 * A component that renders the base price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {propTypes.lineItemUnitType} props.code - The code of the line item
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemBasePriceMaybe = props => {
  const { lineItems, code, intl } = props;
  const isNightly = code === LINE_ITEM_NIGHT;
  const isDaily = code === LINE_ITEM_DAY;
  const isHourly = code === LINE_ITEM_HOUR;
  const isFixed = code === LINE_ITEM_FIXED;
  const translationKey = isNightly
    ? 'OrderBreakdown.baseUnitNight'
    : isDaily
    ? 'OrderBreakdown.baseUnitDay'
    : isHourly
    ? 'OrderBreakdown.baseUnitHour'
    : isFixed
    ? 'OrderBreakdown.baseUnitFixedBooking'
    : 'OrderBreakdown.baseUnitQuantity';

  // Find correct line-items for given code prop.
  // It should be one of the following: 'line-item/night, 'line-item/day', 'line-item/hour', or 'line-item/item'
  // These are defined in '../../util/types';
  // For cart transactions, there may be multiple line-item/item entries (one per listing).
  const unitPurchases = lineItems.filter(item => item.code === code && !item.reversal);

  if (unitPurchases.length === 0) {
    return null;
  }

  // A cart order has one unit line item per listing. Listing them individually
  // duplicates what the cart itself already shows, so collapse them into a
  // single "N items" row and let shipping follow as its own line.
  if (unitPurchases.length > 1) {
    const totalQuantity = unitPurchases.reduce((sum, item) => sum + lineItemQuantity(item), 0);
    const totalAmount = unitPurchases.reduce(
      (sum, item) => sum.plus(item.lineTotal.amount),
      new Decimal(0)
    );
    const combinedTotal = new Money(
      totalAmount.toNumber(),
      unitPurchases[0].lineTotal.currency
    );

    return (
      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="OrderBreakdown.itemsQuantity" values={{ count: totalQuantity }} />
        </span>
        <span className={css.itemValue}>{formatMoney(intl, combinedTotal)}</span>
      </div>
    );
  }

  return unitPurchases.map((unitPurchase, index) => {
    const quantity = unitPurchase?.units
      ? unitPurchase.units.toString()
      : unitPurchase?.quantity
      ? unitPurchase.quantity.toString()
      : null;
    const unitPrice = unitPurchase ? formatMoney(intl, unitPurchase.unitPrice) : null;
    const total = unitPurchase ? formatMoney(intl, unitPurchase.lineTotal) : null;

    const message = unitPurchase?.seats ? (
      <FormattedMessage
        id={`${translationKey}Seats`}
        values={{ unitPrice, quantity, seats: unitPurchase.seats }}
      />
    ) : (
      <FormattedMessage id={translationKey} values={{ unitPrice, quantity }} />
    );

    return quantity && total ? (
      <div key={`base-price-${index}`} className={css.lineItem}>
        <span className={css.itemLabel}>{message}</span>
        <span className={css.itemValue}>{total}</span>
      </div>
    ) : null;
  });
};

export default LineItemBasePriceMaybe;
